import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useEffect, useRef, useState } from "react";
import {
	Animated,
	type LayoutChangeEvent,
	Pressable,
	StyleSheet,
	View,
	useWindowDimensions,
} from "react-native";
import { Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import { INSIGHT_CALLOUT_DIMENSIONS, SHIMMER_ANIMATION } from "./constants";

type InsightCalloutProps = {
	text: string;
	animated?: boolean;
	showLeadingIcon?: boolean;
	showTrailingIcon?: boolean;
	onPress?: () => void;
	onArrowPress?: () => void;
};

export const InsightCallout = ({
	text,
	animated = false,
	showLeadingIcon = true,
	showTrailingIcon = true,
	onPress,
	onArrowPress,
}: InsightCalloutProps) => {
	const theme = useAppTheme();
	const { width: screenWidth } = useWindowDimensions();
	const shimmerTranslateX = useRef(new Animated.Value(-screenWidth)).current;
	const [isSingleLine, setIsSingleLine] = useState(false);
	const SINGLE_LINE_MAX_HEIGHT = 24;

	const handleTextLayout = (e: LayoutChangeEvent) => {
		setIsSingleLine(e.nativeEvent.layout.height <= SINGLE_LINE_MAX_HEIGHT);
	};

	useEffect(() => {
		if (animated) {
			const loop = Animated.loop(
				Animated.timing(shimmerTranslateX, {
					toValue: screenWidth,
					duration: SHIMMER_ANIMATION.duration,
					useNativeDriver: true,
				}),
			);
			loop.start();
			return () => loop.stop();
		}
		shimmerTranslateX.setValue(-screenWidth);
	}, [animated, shimmerTranslateX, screenWidth]);

	const handleArrowPress = onArrowPress ?? onPress;

	return (
		<View
			style={{ overflow: "hidden", position: "relative" }}
			testID="insight-callout"
		>
			<Box
				flexDirection="row"
				alignItems={isSingleLine ? "center" : "flex-start"}
				gap={12}
				style={{
					backgroundColor: theme.colors.primaryContainer,
					padding: INSIGHT_CALLOUT_DIMENSIONS.verticalPadding,
					paddingHorizontal: INSIGHT_CALLOUT_DIMENSIONS.horizontalPadding,
				}}
			>
				<Pressable
					onPress={onPress}
					style={{
						flex: 1,
						flexDirection: "row",
						alignItems: isSingleLine ? "center" : "flex-start",
						gap: 12,
					}}
					accessibilityRole="button"
					accessibilityLabel={text}
				>
					{showLeadingIcon && (
						<MaterialCommunityIcons
							name="lightbulb-on-outline"
							size={24}
							color={theme.colors.onPrimaryContainer}
						/>
					)}
					<Box flex={1}>
						<Text
							variant="bodySmall"
							onLayout={handleTextLayout}
							style={{
								color: theme.colors.onSurface,
							}}
						>
							{text}
						</Text>
					</Box>
				</Pressable>

				{showTrailingIcon && handleArrowPress && (
					<Pressable
						onPress={handleArrowPress}
						style={{
							alignSelf: "stretch",
							alignItems: "center",
							justifyContent: "center",
						}}
						hitSlop={12}
						accessibilityRole="button"
						accessibilityLabel="View insight details"
					>
						<MaterialCommunityIcons
							name="arrow-right"
							size={24}
							color={theme.colors.onPrimaryContainer}
						/>
					</Pressable>
				)}
			</Box>

			{animated && (
				<Animated.View
					style={[
						StyleSheet.absoluteFill,
						{
							transform: [{ translateX: shimmerTranslateX }],
						},
					]}
					pointerEvents="none"
				>
					{/* <LinearGradient
						colors={["transparent", "rgba(255, 255, 255, 0.5)", "transparent"]}
						start={{ x: 0, y: 0 }}
						end={{ x: 1, y: 1 }}
						style={StyleSheet.absoluteFill}
					/> */}
				</Animated.View>
			)}
		</View>
	);
};
